import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { TokenRefreshService } from '../services/token-refresh.service';

/** Evita segundo refresh no retry do mesmo pedido (401 após access ainda inválido). */
export const AUTH_REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

function isAnonymousAuthUrl(url: string): boolean {
  const u = url.toLowerCase();
  return (
    u.includes('/api/auth/refresh') ||
    u.includes('/auth/refresh') ||
    u.includes('/api/auth/login') ||
    u.includes('/api/auth/signin') ||
    u.includes('/auth/signin') ||
    u.includes('/auth/login') ||
    u.includes('/api/auth/signup') ||
    u.includes('/auth/signup') ||
    u.includes('/api/auth/forgot-password') ||
    u.includes('/auth/forgot-password')
  );
}

/**
 * Deve ficar **por último** em `withInterceptors([...])` para receber 401 antes do `unauthorizedInterceptor`.
 * Tenta refresh, atualiza tokens (rotação) e repete o pedido uma vez com o novo access.
 */
export const refreshTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const refreshSvc = inject(TokenRefreshService);
  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      if (req.context.get(AUTH_REFRESH_RETRIED)) {
        return throwError(() => error);
      }
      if (isAnonymousAuthUrl(req.url)) {
        return throwError(() => error);
      }

      return from(refreshSvc.refreshIfPossible()).pipe(
        switchMap((ok) => {
          if (!ok) return throwError(() => error);
          const token = auth.getToken();
          if (!token) return throwError(() => error);
          const retried = req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
            context: req.context.set(AUTH_REFRESH_RETRIED, true),
          });
          return next(retried);
        })
      );
    })
  );
};
